import React, { useState, useEffect } from "react";
import { X, Check, Loader2, Info } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TemplatePreviewCard from "./TemplatePreviewCard";

/**
 * ChangeTemplateDialog — Platform Owner ganti template untuk tenant tertentu.
 *
 * Props:
 *   tenantId, currentKey (template_key aktif tenant)
 *   open, onClose, onChanged(newTemplateKey)
 */
export default function ChangeTemplateDialog({ tenantId, currentKey, open, onClose, onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(currentKey || null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(currentKey || null);
    setLoading(true);
    api.get("/platform/templates", { params: { active_only: true } })
      .then((r) => setTemplates(r.data.items || []))
      .catch((e) => notifyError(e, "Gagal memuat daftar template"))
      .finally(() => setLoading(false));
  }, [open, currentKey]);

  if (!open) return null;

  const apply = async () => {
    if (!selected || selected === currentKey) return;
    setApplying(true);
    try {
      const r = await api.put(`/platform/tenants/${tenantId}/template`, { template_key: selected });
      toast.success(`Template diubah ke "${r.data.template?.template_name}".`);
      onChanged?.(selected);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal ganti template"); }
    finally { setApplying(false); }
  };

  const selectedTpl = templates.find((t) => t.template_key === selected);

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="change-template-dialog">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3 sticky top-0 bg-[#0F0F17] z-10">
          <div>
            <div className="text-white font-display font-black text-lg">Ganti Template</div>
            <div className="text-[11px] text-white/40">Pilih template baru untuk tenant ini. Perubahan tersimpan langsung — belum melakukan deployment.</div>
          </div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10" data-testid="change-template-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-white/40 text-sm text-center py-8">Memuat template…</div>
          ) : (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3">
              {templates.map((t) => (
                <TemplatePreviewCard key={t.template_key} template={t}
                  selected={t.template_key === selected}
                  onClick={() => setSelected(t.template_key)} compact />
              ))}
            </div>
          )}

          {selectedTpl && (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="change-template-details">
              <div className="text-xs text-white/40 uppercase tracking-wider mb-2 flex items-center gap-1">
                <Info size={11} /> Detail Template
              </div>
              <div className="text-white font-display font-black text-lg">{selectedTpl.template_name}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] font-black uppercase tracking-wider text-white/50">{selectedTpl.template_category}</span>
                <span className="text-[10px] font-mono text-white/40">v{selectedTpl.version}</span>
                <span className="text-[10px] font-mono text-white/40">{selectedTpl.template_key}</span>
              </div>
              {selectedTpl.description && (
                <div className="text-sm text-white/70 mt-2">{selectedTpl.description}</div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end sticky bottom-0 bg-[#0F0F17]">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={apply} disabled={!selected || selected === currentKey || applying}
            className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40" data-testid="change-template-apply">
            {applying ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menerapkan…</> : <><Check size={14} className="mr-1" /> Apply Template</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
